import { alpha, type Theme } from "@mui/material/styles";
import type { AppSx } from "../types";
import type { DashboardDecorationPlacement } from "../../../../types/dashboard";

export interface DashboardDecorationViewComponentStyles {
  spacer: AppSx;
  dividerInner: (thickness: number, borderColor?: string) => AppSx;
  image: (objectFit: string, borderRadius?: number) => AppSx;
  tableTitle: AppSx;
  table: (fontSize?: number) => AppSx;
  tableHeaderCell: AppSx;
  panelTitle: AppSx;
  panelBody: AppSx;
  headingLabel: AppSx;
  shell: (item: DashboardDecorationPlacement) => AppSx;
}

export function buildDashboardDecorationViewComponentStyles(
  _theme: Theme
): DashboardDecorationViewComponentStyles {
  return {
    spacer: { width: "100%", height: "100%", opacity: 0 },
    dividerInner: (thickness, borderColor) => ({
      width: "100%",
      borderBottomWidth: thickness,
      borderColor: borderColor || "divider",
    }),
    image: (objectFit, borderRadius) => ({
      maxWidth: "100%",
      maxHeight: "100%",
      width: "100%",
      height: "100%",
      objectFit: objectFit as React.CSSProperties["objectFit"],
      borderRadius: borderRadius ? `${borderRadius}px` : 0,
    }),
    tableTitle: { mb: 1 },
    table: (fontSize) => ({
      "& td, & th": { fontSize: fontSize || 13 },
    }),
    tableHeaderCell: { fontWeight: 700 },
    panelTitle: { mb: 0.75 },
    panelBody: { whiteSpace: "pre-wrap", lineHeight: 1.5 },
    headingLabel: {
      fontSize: "inherit",
      fontWeight: "inherit",
      color: "inherit",
      lineHeight: 1.3,
      width: "100%",
      wordBreak: "break-word",
    },
    shell: (item) => {
      const s = item.style ?? {};
      const horizontalAlign =
        s.textAlign === "center" ? "center" : s.textAlign === "right" ? "flex-end" : "flex-start";
      const verticalAlign =
        s.verticalAlign === "top"
          ? "flex-start"
          : s.verticalAlign === "bottom"
            ? "flex-end"
            : "center";
      const base: AppSx = {
        width: "100%",
        height: "100%",
        fontSize: s.fontSize,
        fontWeight: s.fontWeight,
        color: s.fontColor,
        textAlign: s.textAlign as "left" | "center" | "right" | "justify" | undefined,
        bgcolor: s.backgroundColor,
        border: s.borderWidth ? `${s.borderWidth}px solid ${s.borderColor || "divider"}` : undefined,
        borderRadius: s.borderRadius ? `${s.borderRadius}px` : undefined,
        p: s.padding,
        opacity: s.opacity,
        overflow: item.kind === "table" ? "auto" : "hidden",
      };
      if (item.kind === "panel" || item.kind === "heading" || item.kind === "label" || item.kind === "text") {
        return {
          ...base,
          display: "flex",
          flexDirection: "column",
          justifyContent: verticalAlign,
          alignItems: horizontalAlign,
        };
      }
      return base;
    },
  };
}

export interface DecorationEditDialogComponentStyles {
  panelRoot: AppSx;
  panelHeader: AppSx;
  panelBody: AppSx;
  panelFooter: AppSx;
  colorInput: AppSx;
  tableEditorHeader: AppSx;
  tableScrollBox: AppSx;
  tableColumnField: AppSx;
  tableRowGrid: AppSx;
  addRowButton: AppSx;
  addRowField: AppSx;
  editorPane: AppSx;
  tabs: AppSx;
  tab: AppSx;
  previewTabContainer: AppSx;
  previewTabViewport: AppSx;
}

export function buildDecorationEditDialogComponentStyles(
  _theme: Theme
): DecorationEditDialogComponentStyles {
  return {
    panelRoot: { display: "flex", flexDirection: "column", height: "100%", minHeight: 0 },
    panelHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 1,
      px: 3,
      py: 1.5,
      borderBottom: 1,
      borderColor: "divider",
      flexShrink: 0,
    },
    panelBody: {
      flex: 1,
      minHeight: 0,
      overflow: "hidden",
      px: 3,
      py: 2,
      display: "flex",
      flexDirection: "column",
    },
    panelFooter: {
      display: "flex",
      justifyContent: "flex-end",
      gap: 1,
      px: 3,
      py: 1.5,
      borderTop: 1,
      borderColor: "divider",
      flexShrink: 0,
    },
    colorInput: {
      width: 40,
      height: 36,
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 1,
      p: 0.25,
      cursor: "pointer",
    },
    tableEditorHeader: { mb: 1 },
    tableScrollBox: {
      overflow: "auto",
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 1,
      p: 1,
    },
    tableColumnField: { minWidth: 120 },
    tableRowGrid: { mb: 0.5 },
    addRowButton: { mt: 0.5 },
    addRowField: { mt: 0.5 },
    editorPane: {
      flex: 1,
      minWidth: 0,
      minHeight: 0,
      overflow: "auto",
      display: "flex",
      flexDirection: "column",
      "& .MuiOutlinedInput-input, & .MuiInputBase-input, & .MuiSelect-select": {
        color: "text.primary",
      },
      "& .MuiInputBase-input::placeholder": {
        color: "text.secondary",
        opacity: 1,
      },
    },
    tabs: { mb: 2, minHeight: 36, flexShrink: 0, width: "fit-content", maxWidth: "100%" },
    tab: { textTransform: "none", minHeight: 36 },
    previewTabContainer: {
      flex: 1,
      minHeight: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "auto",
    },
    previewTabViewport: {
      width: "100%",
      maxWidth: 720,
      px: 2,
    },
  };
}

export interface DecorationPaletteComponentStyles {
  list: AppSx;
  item: AppSx;
  dragIcon: AppSx;
  kindIcon: AppSx;
  textBlock: AppSx;
  description: AppSx;
  itemLabel: AppSx;
  header: AppSx;
}

export function buildDecorationPaletteComponentStyles(theme: Theme): DecorationPaletteComponentStyles {
  return {
    list: {
      display: "grid",
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      gap: 0.75,
      width: "100%",
      maxWidth: "100%",
      boxSizing: "border-box",
      overflow: "hidden",
    },
    item: {
      aspectRatio: "1 / 1",
      width: "100%",
      maxWidth: "100%",
      minWidth: 0,
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 0.5,
      px: 0.5,
      py: 0.75,
      cursor: "grab",
      borderRadius: 1.5,
      overflow: "hidden",
      transition: "border-color 0.15s, box-shadow 0.15s, background-color 0.15s",
      "&:hover": {
        borderColor: "secondary.main",
        bgcolor: alpha(theme.palette.secondary.main, 0.06),
        boxShadow: 1,
      },
    },
    dragIcon: { display: "none" },
    kindIcon: {
      flexShrink: 0,
      fontSize: 22,
      lineHeight: 1,
    },
    textBlock: { display: "none" },
    description: { display: "none" },
    itemLabel: {
      fontSize: 10,
      lineHeight: 1.15,
      textAlign: "center",
      fontWeight: 600,
      color: "text.secondary",
      maxWidth: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      px: 0.25,
    },
    header: { mb: 0.5, minWidth: 0 },
  };
}
